import { useTranslation } from 'react-i18next';
import { ArrowUpRightIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { LenderDisplay } from '../types';

export type LenderChipProps = {
  lender: LenderDisplay;
  /**
   * Show the lender's tagline next to the name. Off by default —
   * most slots show just logo + name. Turn on in places where the
   * user is comparing lenders side-by-side.
   */
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const SIZE_CLASSES = {
  sm: {
    wrapper: 'gap-2 ps-1.5 pe-1.5 py-1.5',
    logo: 'size-7',
    name: 'text-xs font-semibold',
    arrow: 'size-5',
    arrowIcon: 'size-3',
  },
  md: {
    wrapper: 'gap-2.5 ps-2 pe-2 py-2',
    logo: 'size-9',
    name: 'text-sm font-semibold',
    arrow: 'size-6',
    arrowIcon: 'size-3.5',
  },
  lg: {
    wrapper: 'gap-3 ps-2.5 pe-2.5 py-2.5',
    logo: 'size-11',
    name: 'text-base font-semibold',
    arrow: 'size-7',
    arrowIcon: 'size-4',
  },
} as const;

/**
 * Logo + name pill for a financing lender. Reused by the carrier
 * landing's partners strip, the apply form's lender picker, the
 * applications table's offer cards, and the review-before-submit
 * "Sending to" summary.
 *
 * When `lender.website` is set the whole chip becomes a clickable
 * link with a trailing external-link arrow, signalling clearly that
 * it'll open the partner's site in a new tab. Without a website
 * URL it renders as a static `<div>` (read-only badge).
 *
 * Visual treatment is intentionally restrained — clean white card,
 * crisp 1px border, a soft brand-color halo around the logo, and a
 * subtle lift-on-hover. Reads as a real, tactile partner card
 * rather than a decorative pill.
 */
export function LenderChip({
  lender,
  showTagline = false,
  size = 'md',
  className,
}: LenderChipProps) {
  const { t } = useTranslation('m-financing');
  const sizes = SIZE_CLASSES[size];
  const isLink = Boolean(lender.website);
  const initial = lender.name.trim().charAt(0).toUpperCase();

  const containerClasses = cn(
    'group inline-flex items-center rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-150',
    isLink &&
      'cursor-pointer hover:-translate-y-px hover:border-gray-300 hover:shadow-md',
    sizes.wrapper,
    className,
  );

  const inner = (
    <>
      {lender.logoUrl ? (
        <img
          src={lender.logoUrl}
          alt={lender.name}
          className={cn('rounded-lg bg-white object-contain', sizes.logo)}
          // Brand-color halo at ~20% alpha — gives each partner a
          // visual signature even when the logo itself is monochrome.
          // `33` is the alpha hex suffix (0x33 / 0xff ≈ 20%).
          style={{ boxShadow: `0 0 0 1.5px ${lender.brandColor}33` }}
          loading="lazy"
        />
      ) : (
        <span
          aria-hidden
          className={cn(
            'flex items-center justify-center rounded-lg font-semibold text-white shadow-sm',
            sizes.logo,
          )}
          style={{ backgroundColor: lender.brandColor }}>
          {initial}
        </span>
      )}
      <div className="flex min-w-0 flex-col leading-tight">
        <span className={cn('text-gray-900', sizes.name)}>{lender.name}</span>
        {showTagline && lender.tagline && (
          <span className="truncate text-xs text-gray-500">
            {lender.tagline}
          </span>
        )}
      </div>
      {isLink && (
        <span
          aria-hidden
          className={cn(
            'ms-1 flex shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors group-hover:bg-gray-100 group-hover:text-gray-700',
            sizes.arrow,
          )}>
          <ArrowUpRightIcon className={cn(sizes.arrowIcon, 'rtl:-scale-x-100')} />
        </span>
      )}
    </>
  );

  if (isLink) {
    return (
      <a
        href={lender.website}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('partner_chip.visit_aria', { name: lender.name })}
        title={t('partner_chip.visit_title', { name: lender.name })}
        className={containerClasses}>
        {inner}
      </a>
    );
  }

  return <div className={containerClasses}>{inner}</div>;
}
